import { useClient } from '@composables/use-clients';
import {
  GetRequestArgsType,
  PostRequestArgsType,
  DeleteRequestArgsType,
} from '@api/v1/types';
import { useTenantStore } from '@stores/tenant-store';
import { urls } from '@api/urls';
import { withTryCatch } from '@api/v1/error-handlers';

export type NewReviewSerializerType = {
  name: string;
  tenantUserIds: number[];
  inputFile: File;
};

export type GetReviewArgsType = GetRequestArgsType & {
  reviewCode: string;
};

export type GetReviewDetailArgsType = GetRequestArgsType & {
  reviewCode: string;
};

export type PostNewReviewPayloadType = NewReviewSerializerType;

export type DeleteReviewArgsType = DeleteRequestArgsType & {
  reviewCode: string;
};

export const apiFetchReviews = async ({
  domain = useTenantStore().domain,
  query = {},
  errorConfig = {},
  callbacks = {},
}: GetRequestArgsType = {}) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.reviews({ domain }),
      query,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiFetchReview = async ({
  domain = useTenantStore().domain,
  reviewCode,
  errorConfig = {},
  callbacks = {},
}: GetReviewArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.reviews({ domain, reviewCode }),
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiFetchReviewDetail = async ({
  domain = useTenantStore().domain,
  reviewCode,
  errorConfig = {},
  callbacks = {},
}: GetReviewArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.reviewDetail({ domain, reviewCode }),
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiCreateReview = async ({
  domain = useTenantStore().domain,
  payload,
  errorConfig = {},
  callbacks = {},
}: PostRequestArgsType<PostNewReviewPayloadType>) => {
  const impl = async () => {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('inputFile', payload.inputFile);
    if (payload.tenantUserIds.length > 0) {
      payload.tenantUserIds.forEach((elem, index) => {
        formData.append(`tenantUserIds[${index}]`, elem.toString());
      });
    }

    const { client } = useClient('auth');
    const res = await client.post({
      url: urls.v1.newReview({ domain }),
      payload: formData,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiDeleteReview = async ({
  domain = useTenantStore().domain,
  reviewCode,
  errorConfig = {},
  callbacks = {},
}: DeleteReviewArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.delete({
      url: urls.v1.reviews({ domain, reviewCode }),
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};
